import {
  pillLabelsFor,
  type RevisionLabel,
  type RevisionScheme,
  revisionSchemeSchema,
  type ScriptVersion,
  scriptVersionSchema,
  type UploadVersionBody,
  uploadVersionResponseSchema,
  type VersionFormat,
  versionFormatSchema,
} from '@noconflict/contracts';
import { z } from 'zod';
import { apiFetch } from '../../shared/api/http';
import type { Supabase } from '../../shared/api/supabase';

/**
 * A version as the customer's card shows it: the row, who uploaded it where a colleague can see them, and once it
 * is confirmed its revisions: the scheme, the format and the labels its pills show (version 1: all of them; a
 * later one: the new ones). Null until it is confirmed.
 */
export type CustomerVersion = ScriptVersion & {
  uploaderName: string | null;
  revisions: { scheme: RevisionScheme; format: VersionFormat; labels: RevisionLabel[] } | null;
};

const scopeRowSchema = z.object({
  script_version_id: z.uuid(),
  scheme: revisionSchemeSchema,
  format: versionFormatSchema,
  labels: z.array(z.string().nullable()),
  new_labels: z.array(z.string().nullable()),
});

/**
 * The project's script versions, oldest first (RLS: whoever may read the project), each with the name of who
 * uploaded it. Version 1 is the original and has no uploader of its own; someone who has left the organization
 * has no name to show.
 */
export async function getScriptVersions(
  client: Supabase,
  projectId: string,
): Promise<CustomerVersion[]> {
  const { data, error } = await client
    .from('script_versions')
    .select('*')
    .eq('project_id', projectId)
    .order('version_number', { ascending: true });
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r) => scriptVersionSchema.parse(r));
  const uploaders = [...new Set(rows.flatMap((r) => (r.created_by ? [r.created_by] : [])))];
  const names: Record<string, string> = {};
  if (uploaders.length > 0) {
    const profiles = await client
      .from('profiles')
      .select('user_id, first_name, last_name')
      .in('user_id', uploaders);
    if (profiles.error) throw new Error(profiles.error.message);
    for (const p of profiles.data ?? []) names[p.user_id] = `${p.first_name} ${p.last_name}`.trim();
  }
  // a customer reads a version's revisions once it is confirmed (RLS), so only confirmed ones are asked for
  const confirmed = rows.filter((r) => r.status === 'confirmed').map((r) => r.id);
  const scopeOf = new Map<string, z.infer<typeof scopeRowSchema>>();
  if (confirmed.length > 0) {
    const found = await client
      .from('script_version_scopes')
      .select('script_version_id, scheme, format, labels, new_labels')
      .in('script_version_id', confirmed);
    if (found.error) throw new Error(found.error.message);
    for (const c of scopeRowSchema.array().parse(found.data ?? []))
      scopeOf.set(c.script_version_id, c);
  }
  return rows.map((r) => {
    const scope = scopeOf.get(r.id);
    return {
      ...r,
      uploaderName: (r.created_by && names[r.created_by]) || null,
      revisions: scope
        ? {
            scheme: scope.scheme,
            format: scope.format,
            labels: pillLabelsFor(scope, r.version_number),
          }
        : null,
    };
  });
}

/** Whether a run is queued or running on the project: no version is deleted while one is. */
export async function getActiveRun(client: Supabase, projectId: string): Promise<boolean> {
  const { data, error } = await client
    .from('runs')
    .select('id')
    .eq('project_id', projectId)
    .in('status', ['queued', 'running'])
    .limit(1);
  if (error) throw new Error(error.message);
  return (data ?? []).length > 0;
}

const entitlementsSchema = z.array(z.object({ versions: z.number().int().nonnegative() }));

/** How many versions the placed orders bought, the main order's and every pack's. */
export async function getVersionsBought(client: Supabase, projectId: string): Promise<number> {
  const { data, error } = await client.rpc('project_entitlements', { p_project_id: projectId });
  if (error) throw new Error(error.message);
  return entitlementsSchema.parse(data)[0]?.versions ?? 0;
}

/** Tells the worker the version's PDF is uploaded; it is measured, numbered and answered (a few seconds). */
export const uploadVersion = (
  token: string,
  projectId: string,
  body: UploadVersionBody,
): Promise<ScriptVersion> =>
  apiFetch(`/api/projects/${projectId}/versions`, uploadVersionResponseSchema, {
    method: 'POST',
    token,
    body: JSON.stringify(body),
  }).then((r) => r.version);

/** Deletes the newest version and everything found in it. */
export async function deleteVersion(
  token: string,
  projectId: string,
  versionId: string,
): Promise<void> {
  await apiFetch(`/api/projects/${projectId}/versions/${versionId}`, z.unknown(), {
    method: 'DELETE',
    token,
  });
}
